import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { NgxGaugeModule } from 'ngx-gauge';
import { DashboardMeasureDetail } from '@models/dashboard-measure-details.model';
import { DashboardMeasureDecile } from '@models/dashboard-measure-deciles.model';
import { MatChipsModule } from '@angular/material/chips';
import { MeasureCalculationDetailsComponent } from './measure-calculation-details';



@Component({
  selector: 'app-measure-details',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    NgxGaugeModule,
    MatChipsModule,
    MeasureCalculationDetailsComponent
  ],
  templateUrl: './measure-details.component.html',
  styleUrls: ['./measure-details.component.css']
})
export class MeasureDetailsComponent implements OnInit {
  @Input() measure!: DashboardMeasureDetail;
  @Input() isSmallPractice: boolean = false;
  @Output() close = new EventEmitter<void>();

  @Input() deciles: DashboardMeasureDecile[] = [];

  isDragging = false;
  temporaryPerformance: number | undefined = 0;

  constructor() { }

  ngOnInit(): void {
    // Initialize temporaryPerformance with the current performance value
    this.reCalculatePoints();
  }

  reCalculatePoints(): void {
      this.temporaryPerformance = this.measure.performance;
    
  }

  getPoints(newValue:number): number {
    return this.getAchievementPointsForMeasure(this.measure,newValue);
  }
 

  closeDetails(): void {
    this.close.emit();
  }

  getFloorText(floor: string): string {
    if (floor == "5")
    {
      return 'Subject to 5-point scoring floor if data completeness is met.'
    }

      if (floor == "7")
    {
      return 'Subject to 7-point scoring floor if data completeness is met.'
    }
    return '';
  }




  onGoToMips(): void {
    // Implement navigation logic to MIPS MVP here
    console.log('Navigate to MIPS MVP for measure:', this.measure.id);
  }

  getFormatedValue(value: number): number {
    var percentage = value * 100;
    var rounded = Math.round(percentage * 100) / 100
    return rounded;
  }

  getFormattedPoints(value: number): number {

    var rounded = Math.round(value * 100) / 100
    return rounded;
  }

  

  convertStringToNumber(value: string | undefined): number {
    // Convert the string to a number
    if (value === null || value === undefined || value.trim() === '') {
      return 0; // or handle the error as needed
    }

    const numberValue = parseFloat(value);
    return numberValue
  }

  isInverseMeasure(measure: DashboardMeasureDetail): boolean {
     if (measure.inverse === undefined) {
      return false; // Default to false if inverse is not defined   
     }

    return measure.inverse.toLowerCase() === 'inverse';
  }



 

  CheckFloorAndCap(value: number, floor: number, has7PointCap: boolean): number {

    // Handle floor logic
    if (floor > 0) {
      // If the calculated points are less than the floor, set it to the floor
      if (value < floor) {
        value = floor;
      }
    }

    // Handle 7-point cap logic
    if (has7PointCap) {
      // Apply 7-point cap logic
      if (value > 7) {
        value = 7;
      }
     
    }
    
    return value;
  }

  /**
 * Calculates the CMS achievement points for a quality measure.
 *
 * @param isInverse      True ⇢ lower performanceRate earns more points.
 * @param floor          Minimum points allowed for this measure.
 * @param has7PointCap   When true, truncate anything above 7 pts.
 * @param measureID      The numeric/alpha measure identifier.
 * @param performanceRateThe clinician’s performance rate.
 */
getAchievementPoints(
  isInverse: boolean,
  floor: number,
  has7PointCap: boolean | undefined,
  measureName: string,
  performanceRate: number,
  isSmallPractice: boolean = false,
  noBenchmark: boolean = false,
  denominator: number = 0,
): number {

  //small practice with no benchmark
  if (isSmallPractice && noBenchmark) {
      return this.CheckFloorAndCap(3,floor, has7PointCap ?? false);
  }

  //small practice with less than 20 patients in denominator
  if(isSmallPractice && denominator< 20 ) {
     return this.CheckFloorAndCap(3,floor, has7PointCap ?? false);
  }

  //large practice with no benchmark
  if (!isSmallPractice && noBenchmark) {
     return this.CheckFloorAndCap(0,floor, has7PointCap ?? false);
  }

  //large practice with denominator < 20
   if (!isSmallPractice && denominator< 20) {
    return this.CheckFloorAndCap(0,floor, has7PointCap ?? false);
  }


  /* 1️⃣  Grab the usable decile rows for this measure */
  const deciles = this.deciles
    .filter(d => d.measureID.toString().toLowerCase() === measureName.toString().toLowerCase())
    .filter(d => d.lowerLimit !== -1 && d.upperLimit !== -1)
    //  For an inverse measure we sort from high → low so that each
    //  successive pair (current , next) always brackets the range.
    .sort((a, b) =>
      isInverse ? b.lowerLimit - a.lowerLimit : a.lowerLimit - b.lowerLimit
    );

  if (!deciles.length) return 0;

  /* ────────────────────────── helpers ────────────────────────── */

  /** CMS rounding/truncation (≤ 0.89 → nearest 0.1,  > 0.9 → 0.9) */
  const adjustFraction = (f: number): number => {
    if (f < 0.01) return 0;
    if (f > 0.9) return 0.9;
    return Math.round(f * 10) / 10;   //  0.0, 0.1, … 0.9
  };

  const pointsWithCaps = (raw: number) =>
    this.CheckFloorAndCap(raw, floor, has7PointCap ?? false);

  /* 2️⃣  Handle the two “extreme” deciles quickly */

  const decile10 = deciles.find(d => d.decile === 10);
  if (decile10 &&
      ((!isInverse && performanceRate >= decile10.lowerLimit) ||
       ( isInverse && performanceRate <= decile10.lowerLimit))) {

    return pointsWithCaps(10);
  }

  const worst = deciles[0];  //  lowest‑limit for normal, highest‑limit for inverse
  if ((!isInverse && performanceRate < worst.lowerLimit) ||
      ( isInverse && performanceRate > worst.lowerLimit)) {

    return pointsWithCaps(0);            // below benchmarkable range
  }

  /* 3️⃣  Find the decile‑pair that brackets the performance rate */

  for (let decileIndex = 0; decileIndex < deciles.length - 1; decileIndex++) {
    const current = deciles[decileIndex];
    const next    = deciles[decileIndex + 1];

    const inRange = isInverse
      ? performanceRate <= current.lowerLimit && performanceRate > next.lowerLimit
      : performanceRate >= current.lowerLimit && performanceRate < next.lowerLimit;

    if (!inRange) continue;

    const lowerBound = current.lowerLimit;
    const upperBound = next.lowerLimit;
    const fraction = isInverse
      ? (lowerBound - performanceRate) / (lowerBound - upperBound)
      : (performanceRate - lowerBound) / (upperBound - lowerBound);

    const partial = adjustFraction(fraction);
    const rawPts  = parseFloat((current.decile + partial).toFixed(2));

    return pointsWithCaps(rawPts);
  }

  /* 4️⃣  Fallback (should never hit) */
  return pointsWithCaps(0);
}



  startDragging(event: MouseEvent): void {
    this.isDragging = true;

    const sliderTrack = (event.target as HTMLElement).closest('.slider-track') as HTMLElement;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!this.isDragging) return;

      const rect = sliderTrack.getBoundingClientRect();
      const offsetX = moveEvent.clientX - rect.left;


      const percentage = Math.max(0, Math.min(1, (offsetX / rect.width)));


      this.temporaryPerformance = percentage;
      this.onPerformanceChange(this.temporaryPerformance);
    };

    const onMouseUp = () => {
      this.isDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  onPerformanceChange(newValue: number) {
    console.log('Performance changed:', newValue * 100);
    console.log('Measure:', this.measure.measureName);
    this.temporaryPerformance = newValue;
    
 
    // You can perform any updates here
  }

  getAchievementPointsForMeasure(measure: DashboardMeasureDetail, newValue: number): number {
    return this.getAchievementPoints(
      this.isInverseMeasure(measure),
      this.convertStringToNumber(measure.floor),
      measure.sevenPointCap,
      measure.name ?? '',
      newValue * 100,
      this.isSmallPractice ?? false,
      measure.noBenchmark?.toLowerCase() === 'true' || measure.noBenchmark?.toLowerCase() === 'no benchmark',
      measure.denominator ?? 0
    );
  }



}
